import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { revalidateLogic } from '@tanstack/react-form';
import { useState } from 'react';

import { mutateAudienceCreate } from '@/api/audience';
import AudienceBaseForm, { audienceBaseFormFields } from '@/app/audiences/AudienceBaseForm';
import { useAppForm } from '@/components/Form';
import { Page } from '@/components/page';
import { Panel } from '@/components/panels';
import Prompt from '@/components/Prompt';
import { exampleAIQuestions } from '@/constants/exampleAIQuestions';
import { toTanStackFormFieldErrors } from '@/error/tanstack-form-field-errors';
import { RuleBuilder, RuleBuilderPreviewDialog } from '@/features/RuleBuilder';
import { RULE_BUILDER_VERSION } from '@/features/RuleBuilder/constants';
import type { AudienceRuleFormValues } from '@/features/RuleBuilder/types';
import useInstanceNavigate from '@/hooks/useInstanceNavigate';
import useLocationState from '@/hooks/useLocationState';
import { useNotification } from '@/providers/Notification';
import { ROUTES } from '@/router';

import { TAM_AUDIENCE_INITIAL_VALUES } from './constants';
import CreateRuleBasedAudienceHeader from './CreateRuleBasedAudienceHeader';

export type FormType = AudienceRuleFormValues;

export const FORM_FIELD = {
  NAME: 'name',
  DESCRIPTION: 'description',
  RULES: 'rules',
  VERSION: 'version',
} as const;

const EMPTY_DEFAULT_VALUES: AudienceRuleFormValues = {
  name: '',
  description: '',
  version: RULE_BUILDER_VERSION,
  rules: {
    dateRange: { unit: 'day', count: 90 },
    orConditions: [],
  },
};

const CreateRuleBasedAudience = () => {
  const { isTAMAudience = false } = useLocationState<{ isTAMAudience: boolean }>();
  const [isCreatingAudience, setIsCreatingAudience] = useState(false);
  const [previewRulesConfig, setPreviewRulesConfig] = useState<string | undefined>(undefined);
  const { pushNotification, removeAllNotifications } = useNotification();
  const navigate = useInstanceNavigate();

  const defaultValues: AudienceRuleFormValues = isTAMAudience
    ? ({ ...EMPTY_DEFAULT_VALUES, ...TAM_AUDIENCE_INITIAL_VALUES } as AudienceRuleFormValues)
    : EMPTY_DEFAULT_VALUES;

  const form = useAppForm({
    defaultValues,
    validationLogic: revalidateLogic(),
    validators: {
      onSubmitAsync: async ({ value }) => {
        removeAllNotifications();
        setIsCreatingAudience(true);

        try {
          const rulesConfig = JSON.stringify({
            rules: value.rules,
            version: value.version,
          });
          await mutateAudienceCreate({
            type: 'rule',
            name: value.name,
            description: value.description,
            rulesConfig,
            isTAM: isTAMAudience,
          });
          navigate(ROUTES.audiences.path, {
            state: {
              successMessage: t`Audience was successfully created.`,
            },
          });
        } catch (error) {
          const fieldErrors = toTanStackFormFieldErrors(error);
          if (fieldErrors) {
            return fieldErrors;
          }

          pushNotification({
            type: 'error',
            message: t`An unknown error has occurred, please try again or contact support.`,
          });
        } finally {
          setIsCreatingAudience(false);
        }
      },
    },
  });

  const handlePreview = async () => {
    try {
      const orConditionsErrors = form.state.fieldMeta['rules.orConditions']?.errors ?? [];
      if (orConditionsErrors.length > 0) {
        pushNotification({
          type: 'error',
          message: t`Please fix rule errors before previewing`,
        });
        return;
      }
      const values = form.state.values;
      const rulesConfig = JSON.stringify({
        rules: values.rules,
        version: values.version,
      });
      removeAllNotifications();
      setPreviewRulesConfig(rulesConfig);
    } catch {
      pushNotification({
        type: 'error',
        message: t`Please fix rule errors before previewing`,
      });
    }
  };

  return (
    <Page
      title={t`Configure - Create Audience`}
      exampleAIQuestions={exampleAIQuestions('audience')}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          form.handleSubmit();
        }}
      >
        <form.AppForm>
          <form.Subscribe
            selector={(state) => ({ isDirty: state.isDirty, isSubmitting: state.isSubmitting })}
          >
            {({ isDirty, isSubmitting }) => (
              <Prompt
                message={t`Are you sure you want to exit without saving your new Audience?`}
                shouldPreventNavigation={isDirty && !isSubmitting}
              />
            )}
          </form.Subscribe>
          <div className="flex flex-col justify-center gap-6">
            <CreateRuleBasedAudienceHeader
              isTAMAudience={isTAMAudience}
              isCreatingAudience={isCreatingAudience}
              onPreview={handlePreview}
            />
            <Panel title={<Trans>Audience Details</Trans>}>
              <AudienceBaseForm
                form={form}
                fields={audienceBaseFormFields}
                audience={undefined}
                autoFocus
              />
            </Panel>
            <Panel>
              <RuleBuilder disabled={isCreatingAudience} />
            </Panel>
          </div>
        </form.AppForm>
      </form>
      <RuleBuilderPreviewDialog
        rulesConfig={previewRulesConfig}
        onClose={() => setPreviewRulesConfig(undefined)}
      />
    </Page>
  );
};

export default CreateRuleBasedAudience;
